import { AfterViewInit, Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { OnlineStatusService, OnlineStatusType } from "ngx-online-status";
import { ToastrService } from 'ngx-toastr';
import { ResetPasswordDialogComponent } from '../reset-password-dialog/reset-password-dialog.component';
import { AuthService } from '../shared/auth.service';
import { LocalStorageService } from '../shared/local-storage.service';
import { LookupDataService } from '../shared/lookup-data.service';
import { Insights } from '../shared/insights.service';

/**
 * Login component
 * Basic login form with Angular reactive forms module
 * Routes from here to menu on successful login, to signup component when clicking signup link, opens reset-password-dialog when link clicked
 */
@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    standalone: false
})
export class LoginComponent implements AfterViewInit {

  loginForm = new FormGroup({
    username: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required])
  });

  /**
   * Indicates whether the progress bar should show (waiting for login to complete)
   */
  sending: boolean = false;

  /**
   * Show or hide password in the password form box
   */
  hide: boolean = true;

  constructor(
    public dataSync: LocalStorageService,
    private router: Router,
      private auth: AuthService,
       private onlineStatusService: OnlineStatusService,
        private lookup: LookupDataService,
         private toastr: ToastrService,
         public dialog: MatDialog,
        private insights: Insights) { }


  /**
   * If the user has come here from the signup process this method will catch it and automatically log them in.
   */
  ngAfterViewInit(): void {
    if (this.dataSync.sign_up()) {
      this.dataSync.setLoginStatus(this.dataSync.current_user().userName).then(res => {
        if (res) {
          this.initializeSession();
          this.dataSync.sign_up.set(false);
          this.toastr.success('Welcome to FishVue FLOAT! We\'ll send you an email from fedelog@psmfc.org to confirm your email', 'Sign-up complete!', {disableTimeOut: true, positionClass: 'toast-bottom-right'});
        } 
      });  
    }
  }

  /**
   * 
   * @param key Pressed key
   * If enter key is pressed attempt a login
   */
  handleKeyUp(key: any) {
    if (key.keyCode == 13) this.onLogin();
  }


  /**
   * <b>Login process</b>
   * <br><br>
   * If online then: 
   *  1. Post login details to server
   *  2. Check for user profile stored on device
   *  3. Set login status of user
   *  4. Set some static variables in the Local Storage service
   *  5. Update this user's information/profile then initialize the login session. 
   * 
   * If offline:
   *  1. Verify the user details with locally stored profile
   *  2. Set the login status then initialize the session
   * 
   * Offline login fails if details are wrong or if user is not stored on device, that is they signed up on a different device and never logged into this device 
   */
  onLogin() {
    this.sending = true;
    if (this.loginForm.valid) {
      if (this.onlineStatusService.getStatus() == OnlineStatusType.ONLINE) {
        //Online, make an API call to login
        this.insights.trackEvent('Login', 'Attempting to login');
        let loginName = (this.loginForm.get('username')!.value ?? "").trim();
        this.auth.postLogin({"password": this.loginForm.get('password')!.value, "username": loginName}).subscribe({next: (response) => {
          //Check if user is stored on this device and create their profile if it isn't
          this.dataSync.checkForUserInfo(response, this.loginForm.get('password')!.value!).then(_ => {    

            //Update login_status document with true logged in status and logged in user data       
            this.dataSync.setLoginStatus(loginName).then(_ => {
              let user = this.dataSync.current_user();
              user.accessToken = response.accessToken;
              user.refreshToken = response.refreshToken;
              user.licenseNumber = response.licenseNumber ? response.licenseNumber : response.properties.licenseNumber; 
              user.phoneNumber = response.phone ? response.phone : response.properties.phone;
              user.couchDbConnection = response.couchDbConnection;
              this.dataSync.current_user.set(user);

              console.log('Refresh token restored');

              //Update this user's stored profile data then initialize the session
              this.dataSync.updateUser(false, true).then(_ => this.initializeSession());
            });  
          });
        }, error: (err) => {
          console.log(err)
          if (err.status && err.status > 399 && err.status < 500) {
            this.toastr.error(err.error, 'Login failed');
            this.sending = false;
          } else if ((err.error?.code ?? 0) == 23 || err.status == 504) {
            this.toastr.warning('Reverting to offline mode', 'Timeout');
            this.offlineLogin();
          }        
        }});

      } else {
        //Offline, sign-in with local profile
        //No need to update any stored data since no data is received from the server
        this.offlineLogin();
      }
      
    } else {
      this.toastr.error('Missing credentials', 'Login failed');
      this.sending = false;
    }
  }


  offlineLogin() {
    let loginName = (this.loginForm.get('username')!.value ?? "").trim();
    this.dataSync.verifyLocalProfile(loginName, this.loginForm.get('password')!.value!).then(result => {
        if (result) {
          console.log('Login successful');
          this.dataSync.setLoginStatus(loginName).then(res => {
            if (res) this.initializeSession();
          })           
        } else {
          this.toastr.error('Invalid credentials or user not stored on device, ensure you have successfully logged into this device before and your credentials are correct', 'Login failed');
          this.sending = false;
        }
      });
  }
  


  /**
   * Retrieve lookup data and all user data, check for a migration (versions after 1.1.0 changed the inner storage scheme), perform a data sync, then go to menu
   */
  initializeSession(): void {
    //Open database, get all user data
    //Get lookup values from server, or pull from database if offline
    this.insights.trackEvent('Init session', 'Initializing session');
    this.lookup.retrieveLookupData();

    this.dataSync.checkForMigration().then((res: boolean) => {
      if (res) {
        this.insights.trackEvent('Open database', 'Open database post login');
        this.dataSync.openLocalStorage();
        this.dataSync.syncUserDatabase(true);
        this.dataSync.sync_promise.finally(() => this.router.navigate(['menu']));


      } else {
        //Need to perform a migration
        console.log('Performing migration...');
        this.insights.trackEvent('Migration on login', 'Attempting an HMS migration on login');
        this.dataSync.migrateToHMSCombined().then(() => {
          this.dataSync.openLocalStorage();
          this.dataSync.syncUserDatabase(true);
          this.dataSync.sync_promise.finally(() => this.router.navigate(['menu']));

        });        
      }
    })
  }


  /**
   * Open the reset-dialog-component and take it from there
   */
  forgotPassword() {
    this.dialog.open(ResetPasswordDialogComponent, {
      width: '600px',
      height: '600px',
      disableClose: true,
    });
  }

}
